import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@connectrpc/connect-query';
import { getMe } from '@release/connect-gen/legacy/releaseid/useridgw-UserIDGatewayService_connectquery';

import { Greeting, TimeSection } from '../types';

// 表示している日付・時刻と時間帯の見た目を実時刻に追随させる間隔。分表示に合わせて1分。
const DATE_REFRESH_INTERVAL_MS = 60_000;

const getCurrentTimeSection = (now: Date): TimeSection => {
  const hour = now.getHours();
  if (hour >= 6 && hour < 10) {
    return 'morning';
  }
  if (hour >= 10 && hour < 16) {
    return 'noon';
  }
  if (hour >= 16 && hour < 18) {
    return 'evening';
  }
  return 'night';
};

const greetingsMap = {
  morning: ['おはようございます'],
  noon: ['こんにちは', 'お疲れさまです'],
  evening: ['こんばんは', 'おかえりなさい'],
  night: ['こんばんは', 'おかえりなさい'],
};

const encouragementMessagesMap = {
  morning: ['今日も一日、頑張りましょう！', '良い一日を！'],
  noon: ['午後も引き続き頑張りましょう！'],
  evening: ['今日もお疲れさまでした'],
  night: ['今日もお疲れさまでした', '夜遅くまでお疲れ様です'],
};

export const useHomeGreeting = (): Greeting => {
  const me = useQuery(getMe);
  const [greeting, setGreeting] = useState<string>('');
  const [encouragement, setEncouragement] = useState<string>('');

  // 分まで表示し、時間帯で見た目も変わるため、開きっぱなしでも実時刻に追随させる。
  const [date, setDate] = useState<Date>(() => new Date());

  useEffect(() => {
    const timerId = setInterval(() => setDate(new Date()), DATE_REFRESH_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, []);

  const timeSection = useMemo(() => {
    return getCurrentTimeSection(date);
  }, [date]);

  useEffect(() => {
    if (!me.data?.user?.info) return;
    // lastName は空文字を拒否する制約がない（users.last_name は NOT NULL だが空文字は通る）。
    // 名前がない場合もカード自体は出したいので、名前部分を省いた挨拶にフォールバックする。
    const { lastName } = me.data.user.info;

    const greetingOptions = greetingsMap[timeSection];
    const encouragementOptions = encouragementMessagesMap[timeSection];

    const greeting = greetingOptions[Math.floor(Math.random() * greetingOptions.length)];
    const encouragement = encouragementOptions[Math.floor(Math.random() * encouragementOptions.length)];

    setGreeting(lastName ? `${greeting}、\n${lastName} さん` : greeting);
    setEncouragement(encouragement);
  }, [me.data?.user?.info, timeSection]);

  if (greeting === '' || encouragement === '' || !me.data?.user?.info) {
    return {
      isReady: false,
    };
  }

  return {
    isReady: true,
    date,
    timeSection,
    greeting,
    encouragement,
  };
};
